"use client";

import { AnimatePresence, motion } from "motion/react";
import { useSearchParams } from "next/navigation";
import { ReactNode, Suspense, useEffect, useState } from "react";

import { AccessVisual } from "@/components/AccessVisual";
import { SiteFooter, SiteHeader } from "@/components/site/Chrome";
import type { RoleVisualData } from "@/components/RoleDetectionCard";
import { CreatorInviteForm } from "@/components/CreatorInviteForm";
import { DemoRequestForm } from "@/components/DemoRequestForm";
import { TalentManagementForm } from "@/components/TalentManagementForm";

type Role = {
  id: "brand" | "talent" | "creator";
  tab: string;
  title: ReactNode;
  description: ReactNode;
  badges: string[];
  visual: RoleVisualData;
  stepsTitle: ReactNode;
  steps: { title: string; desc: string }[];
  form: ReactNode;
};

const Accent = ({ children }: { children: ReactNode }) => (
  <span className="text-brand-700 relative">
    {children}
    <span className="absolute -bottom-1 left-0 right-0 h-[3px] rounded-full bg-brand-400/60" />
  </span>
);

const ROLES: Role[] = [
  {
    id: "brand",
    tab: "Brand and campaign services",
    title: (
      <>
        Book a <Accent>Brand Demo</Accent>
      </>
    ),
    description: (
      <>
        Launch creator-led paid media with full approvals, brand safety
        controls, and clear reporting.{" "}
        <span className="text-gray-400">
          Built for performance marketing teams at brands and agencies.
        </span>
      </>
    ),
    visual: {
      image: "/request-access/brand.jpg",
      alt: "Triathlete running past a Gatorade flag and barrier",
      boxes: [
        { label: "GATORADE FLAG", logo: false, left: 19.75, top: 0, width: 13.5, height: 48.6 },
        { label: "GATORADE LOGO", logo: true, left: 22.5, top: 30, width: 10.8, height: 11.4 },
        { label: "GATORADE LOGO", logo: true, left: 4, top: 48, width: 13.5, height: 18.6, tagBelow: true },
      ],
      widget: { seconds: "4.3", views: "38k", payout: "$114.00" },
      widgetAt: "br",
    },
    badges: ["Frame-by-frame AI analysis", "Performance-based payouts", "Brand safety controls"],
    stepsTitle: (
      <>
        What you&apos;ll see in <span className="text-brand-700">15 minutes</span>
      </>
    ),
    steps: [
      { title: "How campaigns run", desc: "Brief, creator selection, approvals, launch and reporting" },
      { title: "Brand safety & control", desc: "Approvals, category exclusions, audit trail" },
      { title: "Reporting & payouts", desc: "Campaign status, summaries, payout ledger view" },
    ],
    form: <DemoRequestForm />,
  },
  {
    id: "talent",
    tab: "Talent management",
    title: (
      <>
        Partner with <Accent>Fitted</Accent>
      </>
    ),
    description: (
      <>
        We are now accepting manager and agency submissions for our preferred
        partner program.
      </>
    ),
    visual: {
      image: "/request-access/talent.jpg",
      alt: "Man in an Adidas Originals track jacket",
      boxes: [
        { label: "ADIDAS JACKET", logo: false, left: 12.5, top: 33.5, width: 57.5, height: 66.5 },
        { label: "ADIDAS LOGO", logo: true, left: 24.75, top: 63, width: 16.25, height: 12.5 },
      ],
      widget: { seconds: "5.9", views: "58k", payout: "$174.00" },
      widgetAt: "br",
    },
    badges: ["Payouts tied to visibility and views", "Brand campaigns matched to your creators", "Clear campaign reporting"],
    stepsTitle: (
      <>
        How the <span className="text-brand-700">partner program</span> works
      </>
    ),
    steps: [
      { title: "Tell us about your agency", desc: "Share your contact details and how much talent you manage" },
      { title: "We review your submission", desc: "Our partnerships team reviews every agency and reaches out" },
      { title: "Bring your roster to campaigns", desc: "Partners get matched with brand campaigns that pay for real visibility" },
    ],
    form: <TalentManagementForm />,
  },
  {
    id: "creator",
    tab: "Creator",
    title: (
      <>
        Join the <Accent>Creator Invite List</Accent>
      </>
    ),
    description: (
      <>
        We&apos;ll be accepting individual applications for marketplace
        activations soon. Please provide your details below to join the
        invite list.
      </>
    ),
    visual: {
      image: "/request-access/creator.jpg",
      alt: "Creator celebrating at a race event beside a Gatorade tent",
      boxes: [
        { label: "GATORADE TENT", logo: false, left: 0, top: 51.6, width: 29.3, height: 30 },
        { label: "GATORADE LOGO", logo: true, left: 2, top: 81.6, width: 27.3, height: 8.4 },
        { label: "NIKE SHORTS", logo: false, left: 29.3, top: 66.6, width: 35.7, height: 26 },
        { label: "NIKE SWOOSH", logo: true, left: 56.75, top: 86.2, width: 4.5, height: 2.3 },
      ],
      widget: { seconds: "5.6", views: "64k", payout: "$192.00" },
      widgetAt: "tl",
    },
    badges: ["Paid for real brand visibility", "Frame-by-frame AI analysis", "Brands that fit your content"],
    stepsTitle: (
      <>
        What happens <span className="text-brand-700">next</span>
      </>
    ),
    steps: [
      { title: "Join the invite list", desc: "Share your details and your largest channel on each platform" },
      { title: "Applications open", desc: "We'll email you as soon as individual applications open" },
      { title: "Get matched with brands", desc: "Earn from the seconds a brand is visible in your content, multiplied by views" },
    ],
    form: <CreatorInviteForm />,
  },
];

/**
 * Preselects a tab from ?role=brand|talent|creator (the footer Solutions links).
 * Kept in its own Suspense boundary so the rest of the page stays prerendered.
 */
function RoleFromUrl({ onRole }: { onRole: (id: Role["id"]) => void }) {
  const role = useSearchParams().get("role");
  useEffect(() => {
    const match = ROLES.find((r) => r.id === role);
    if (match) onRole(match.id);
  }, [role, onRole]);
  return null;
}

export function RequestAccess() {
  const [activeId, setActiveId] = useState<Role["id"] | null>(null);
  const active = ROLES.find((role) => role.id === activeId) ?? null;

  return (
    <div className="min-h-screen flex flex-col selection:bg-brand-400/30 selection:text-brand-900">
      <Suspense fallback={null}>
        <RoleFromUrl onRole={setActiveId} />
      </Suspense>
      {/* Grows to fill the viewport so the footer stays at the bottom */}
      <SiteHeader section="company" active="/request-access" />
      <main className="flex-1">

        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 sm:pt-14">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center text-5xl lg:text-6xl font-display font-semibold tracking-tight text-gray-900 leading-[1.08] mb-10"
          >
            Request <Accent>access</Accent>
          </motion.h1>

          {/* Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            role="radiogroup"
            aria-label="What best describes your interest?"
            className="grid grid-cols-3 gap-2 sm:gap-3 max-w-3xl mx-auto"
          >
            {/* Native radios (visually styled as buttons): reachable with Tab in
                every browser, including Safari, and arrow keys switch roles. */}
            {ROLES.map((role) => {
              const selected = activeId === role.id;
              return (
                <label key={role.id} className="relative cursor-pointer">
                  <input
                    type="radio"
                    name="interest"
                    value={role.id}
                    checked={selected}
                    onChange={() => setActiveId(role.id)}
                    className="peer sr-only"
                  />
                  <span
                    className={`flex h-full min-h-14 items-center justify-center text-center rounded-2xl sm:rounded-full px-2 sm:px-5 py-2.5 text-xs sm:text-sm font-medium leading-snug transition-all border peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2 ${
                      selected
                        ? "bg-brand-900 border-brand-900 text-white shadow-md shadow-brand-900/20"
                        : "bg-white/80 border-gray-200 text-gray-700 hover:border-brand-500 hover:text-brand-900 hover:-translate-y-0.5"
                    }`}
                  >
                    {role.tab}
                  </span>
                </label>
              );
            })}
          </motion.div>

          {active === null && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="text-center text-gray-500 text-lg mt-8 pb-24"
            >
              What best describes your interest?
            </motion.p>
          )}
        </div>

        <AnimatePresence mode="wait">
          {active && (
            <motion.div
              key={active.id}
              id="request-access-panel"
              role="region"
              aria-label={active.tab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Role intro */}
              <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-14 text-center">
                <h2 className="text-4xl lg:text-5xl font-display font-semibold tracking-tight text-gray-900 leading-[1.1] mb-4">
                  {active.title}
                </h2>
                <p className="text-gray-500 text-lg max-w-xl mx-auto leading-relaxed">
                  {active.description}
                </p>
              </div>

              {/* Visual + form */}
              <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-12 mb-20">
                <div className="grid lg:grid-cols-[420px_1fr] rounded-3xl overflow-hidden shadow-2xl shadow-brand-900/10 border border-gray-100">
                  <AccessVisual badges={active.badges} visual={active.visual} />
                  <div className="bg-white px-6 py-10 sm:px-8 lg:px-12 lg:py-12">
                    {active.form}
                  </div>
                </div>
              </div>

              {/* Steps */}
              <div className="border-t border-gray-100 bg-gray-50/60 py-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                  <h2 className="text-center text-4xl lg:text-5xl font-display font-semibold text-gray-900 mb-12">
                    {active.stepsTitle}
                  </h2>
                  <div className="grid md:grid-cols-3 gap-6">
                    {active.steps.map((step, i) => (
                      <motion.div
                        key={step.title}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.1 }}
                        className="bg-white rounded-2xl p-8 text-center border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="w-10 h-10 rounded-full bg-brand-900 text-white font-display font-bold text-lg flex items-center justify-center mx-auto mb-5">
                          {i + 1}
                        </div>
                        <h3 className="font-display font-semibold text-gray-900 text-lg mb-3">
                          {step.title}
                        </h3>
                        <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <SiteFooter />
    </div>
  );
}
